import { formatPeso, type LocalDate } from '@tahi/core';
import { ordinal, untilText } from './format';

const peso = (minor: number) => formatPeso(minor);

export const copy = {
  moduleName: 'Budget',
  loading: 'Loading…',
  nothingHere: 'Nothing here yet.',
  save: 'Save',
  undo: 'Undo',
  delete: 'Delete',
  confirmDelete: 'Tap again to delete',
  back: 'Back',

  // Dashboard and tiles
  whatsLeft: "What's left",
  whatsLeftSpoken: 'What you have left',
  safeToSpend: 'Safe to spend',
  nextPayday: 'Next payday',
  allIncome: 'This cutoff',
  leftUntil: (until: string) => `left ${until}`,
  overLine: (on: string) => `short · starts fresh ${on}`,
  spentOf: (spent: number, total: number) => `Spent ${peso(spent)} of ${peso(total)}`,
  incomeAndSpent: (income: number, spent: number) => `${peso(income)} in, ${peso(spent)} spent`,
  afterPayments: (reserved: number) => `after ${peso(reserved)} in payments still due`,
  noIncomeYet: "Add your salary to see what's left each cutoff.",
  noFundsHint: 'Add a spending limit for a week, a cutoff or a month.',
  paydayBanner: (amount: number) => `Payday. Did ${peso(amount)} come in?`,
  answer: 'Answer',
  recent: 'Recent',
  dashboardNav: 'Budget pages',
  menuLeft: 'left',
  sourcesCount: (n: number) => (n === 1 ? '1 source' : `${n} sources`),
  limitsCount: (n: number) => (n === 1 ? '1 limit' : `${n} limits`),

  // Pages
  cashFlow: 'Cash flow',
  funds: 'Spending limits',
  income: 'Income',
  settings: 'Settings',
  settingsTitle: 'Budget settings',

  // Funds
  addFund: 'Add spending limit',
  editFund: 'Edit spending limit',
  noFunds:
    'No spending limits yet. A limit is what you allow yourself to spend in a week, a cutoff or a month.',
  fundName: 'Name',
  fundAmount: 'Limit each period',
  fundPeriod: 'Period',
  refillFrom: 'Refills on paydays of',
  firstSalary: 'First salary',
  defaultBadge: 'Default',
  makeDefault: 'Make default',
  fundCard: (name: string, spokenLeft: string, spokenUntil: string) =>
    `${name}, ${spokenLeft} left until ${spokenUntil}`,
  fundEntries: 'Spending under this limit',
  noFundEntries: 'Nothing spent under this limit this period.',
  deletedFund: (name: string) => `Deleted ${name}.`,
  fundPeriodLine: (period: string, range: string) => `${period} · ${range}`,

  // Income
  addSalary: 'Add salary',
  addBonus: 'Add bonus',
  addOtherIncome: 'Add other income',
  editSource: 'Edit income',
  newSalary: 'New salary',
  newBonus: 'New bonus',
  waiting: 'Waiting for you',
  sources: 'Sources',
  noSources: 'No income added yet.',
  nextOn: (day: string, amount: number) => `Next: ${day}, ${peso(amount)}`,
  paydayRow: (source: string, day: string, amount: number) => `${source}, ${day}, ${peso(amount)}`,
  otherIncomeLabel: 'Other income',
  addedIncome: (amount: number) => `Added ${peso(amount)} income.`,
  editOtherIncome: 'Edit income',
  savedIncome: 'Saved.',
  deletedIncome: (amount: number) => `Deleted ${peso(amount)} income.`,
  sourceName: 'Name',
  sourceNamePlaceholder: 'Salary · Company',
  howOften: 'How often',
  twiceAMonth: 'Twice a month',
  monthly: 'Monthly',
  everyTwoWeeks: 'Every 2 weeks',
  weekly: 'Weekly',
  sameDatesEveryYear: 'Same dates every year',
  everyFewMonths: 'Every few months',
  firstPayday: 'First payday (day of the month)',
  secondPayday: 'Second payday (31 = last day)',
  paydayDay: 'Payday (day of the month, 31 = last day)',
  amountOn: (day: string) => `Amount on the ${day}`,
  amount: 'Amount',
  recentPayday: 'A recent payday',
  usualAmount: (i: number) => (i === 0 ? 'Usual amount' : `Usual amount ${i + 1}`),
  monthN: (i: number) => (i === 0 ? 'Month' : `Month ${i + 1}`),
  dayN: (i: number) => (i === 0 ? 'Day' : `Day ${i + 1}`),
  addDate: 'Add another date',
  removeDate: (i: number) => `Remove date ${i + 1}`,
  everyHowManyMonths: 'Every how many months',
  lastTimeItCame: 'Last or next time it came',
  weekendRule: 'If payday falls on a weekend',
  dayBefore: 'Business day before',
  dayAfter: 'Business day after',
  trackFrom: 'Track paydays from',
  lastPaydayBy: 'Last payday on or before (optional)',
  lastPaydayByHint: 'No paydays after this day. Leave it empty if it keeps going.',
  noEndDate: 'No end date',
  endsOn: (day: string) => `Ends ${day}`,
  notePlaceholder: 'Note (optional)',
  dateLabel: 'Date',

  // Payday sheet
  paydayQuestion: (amount: number) => `Payday. Did ${peso(amount)} come in?`,
  expectedOn: (amount: number, day: string) => `Expected ${peso(amount)} on ${day}.`,
  receivedTitle: (amount: number) => `Received ${peso(amount)}`,
  skippedTitle: 'Skipped this time',
  skippedNote: 'The usual amount stays the same for next time.',
  expectedWas: (amount: number) => `expected ${peso(amount)}`,
  confirm: 'Confirm',
  differentAmount: 'Different amount',
  cameAlready: 'It came already',
  didntCome: "It didn't come",
  notYet: 'Not yet',
  changeAmount: 'Change amount',
  undoSkip: 'Undo skip',
  dayItCame: 'Day it came',
  receivedToast: (amount: number) => `Received ${peso(amount)}.`,
  skippedToast: 'Skipped. Next time stays the same.',
  undoneToast: 'Back to expected.',
  paydayNotFound: 'This payday is no longer on the schedule.',

  // Cash flow
  period: 'Period',
  monthOption: 'Month',
  cutoffOption: (n: number) => `Cutoff ${n}`,
  prevMonth: 'Previous month',
  nextMonth: 'Next month',
  projection: 'Projection',
  history: 'History',
  soFar: 'So far',
  /** Current and future periods: the income total's parts ("₱18,000 expected + ₱2,000 received"). */
  incomeParts: (pending: number, received: number) =>
    [
      pending > 0 ? `${peso(pending)} expected` : null,
      received > 0 ? `${peso(received)} received` : null,
    ]
      .filter(Boolean)
      .join(' + '),
  /** Past periods: paydays compared like-for-like (salary and bonuses, received vs their defaults). */
  paydaysVsExpected: (received: number, expected: number) =>
    `Salary and bonuses: ${peso(received)} of ${peso(expected)} expected`,
  otherIncomeTotal: (amount: number) => `Other income: ${peso(amount)}`,
  leftAfterCommitments: 'Left after commitments',
  otherSpending: 'Other spending',
  whatYouHaveLeft: 'What you have left',
  itemsOf: (line: string) => `${line} items`,
  vsExpected: (diff: number) =>
    diff === 0
      ? 'Same as expected'
      : diff > 0
        ? `${peso(diff)} more than expected`
        : `${peso(-diff)} less than expected`,
  pendingNote: 'Paydays not confirmed yet count at their usual amount.',
  tagExpected: 'expected',
  tagNotConfirmed: 'not confirmed',
  tagReceived: 'received',
  tagSkipped: 'skipped',
  tagOther: 'other',
  tagEstimated: 'estimated',
  tagPaid: 'paid',
  tagOverdue: 'overdue',
  noIncomeInPeriod: 'No paydays in this period.',

  // Settings
  autoDeductLabel: 'Count new spending under the default limit',
  autoDeductHelp:
    'Turning this off or on affects new entries only. Past deductions stay as they are.',
  autoDeductOn: (fund: string) => `New spending counts under ${fund}.`,
  autoDeductNoFund: 'Pick a default limit so new spending counts under it.',
  autoDeductOff: 'Spending is tracked only, unless you pick a limit on the entry.',
  defaultFund: 'Default limit',
  noDefaultFund: 'None',

  // What's left tile settings
  whatsLeftSource: 'Uses',
  perCutoff: 'Per cutoff',
  perMonth: 'Per month',

  // Spending quick add
  fundLabel: 'Limit',
  fundHintDefault: (fund: string) => `Leave it to count it under ${fund}.`,
  fundHintNone: 'Not counted under a limit unless you pick one.',
  fundHintMoveNone: 'Not under any limit. Pick one to move it there.',
  fundRemoved: 'That limit was removed. Pick another, or leave it in none.',

  thisMonth: 'This month',
  fundRequired: 'Give it a name, like Allowance or Groceries.',
  enterAmount: 'Enter an amount above ₱0.',
  keptIn: 'Kept in',
  keptInHint: 'The first one is where spending under this limit comes from.',
  keptInTotal: (amount: number) => `${peso(amount)} in its accounts`,

  // v0.5 Your money / Accounts
  yourMoney: 'Your money',
  accounts: 'Accounts',
  addAccount: 'Add account',
  editAccount: 'Edit account',
  addYourAccounts: 'Add your accounts (Cash, GCash, your bank) to see your real money.',
  noAccounts: 'No accounts yet.',
  accountName: 'Name',
  accountNamePlaceholder: 'GCash, BDO savings, Cash',
  accountType: 'Type (optional)',
  accountTypes: { cash: 'Cash', ewallet: 'E-wallet', bank: 'Bank' } as Record<string, string>,
  balanceNow: 'Balance now',
  balanceAsOf: 'As of',
  balanceHelp: 'What the account holds right now. It is your starting point, not income.',
  total: 'Total',
  moneyOnHand: 'Money on hand',
  transfer: 'Transfer',
  transferTitle: 'Move money',
  from: 'From',
  to: 'To',
  fee: 'Fee (optional)',
  transferNote: 'Never counts as spending or income.',
  transferred: (amount: number, from: string, to: string) =>
    `Moved ${peso(amount)} from ${from} to ${to}.`,
  adjustBalance: 'Adjust balance',
  realBalance: 'Real balance now',
  adjustHelp: (now: number) =>
    `Tahi has ${peso(now)}. Type what the account really holds; the difference is recorded as an adjustment.`,
  adjustInterestHint: 'For interest, use Interest received instead.',
  adjusted: (diff: number) =>
    diff >= 0 ? `Added ${peso(diff)} adjustment.` : `Took off ${peso(-diff)} adjustment.`,
  movements: 'Movements',
  noMovements: 'Nothing has moved in or out since the balance you typed.',
  openingLine: (amount: number, day: string) => `Balance on ${day}: ${peso(amount)}`,
  deletedAccount: (name: string) => `Deleted ${name}.`,
  deletedMovement: 'Deleted.',
  movementLabels: {
    expense: 'Spent',
    refund: 'Refund',
    income: 'Income',
    transfer: 'Transfer',
    card_payment: 'Card payment',
    deposit: 'Received',
    interest: 'Interest received',
    adjustment: 'Adjustment',
  } as Record<string, string>,
  payFrom: 'Pay from',
  payFromHelp: 'Spending and payments come out of this account unless you pick another.',
  receiveInto: 'Receive into',
  receiveIntoSourceHint: 'Leave it empty to use the Receive into account in Budget settings.',
  receiveIntoHelp: 'Salary and other income land here unless the income says otherwise.',
  noneOption: 'None',
  useFunds: 'Use spending limits',
  useFundsHelp:
    'Set an amount you allow yourself per week, cutoff or month (like Allowance). Off: Budget shows your money, income and payments only. Limits you made are kept.',

  // Safe to spend (v0.5)
  safeUntil: (day: string) => `Safe to spend until ${day}`,
  onHandAndDue: (onHand: number, due: number) =>
    `${peso(onHand)} on hand · ${peso(due)} due before payday`,
  plusExpected: (amount: number) => ` · ${peso(amount)} salary not confirmed yet`,
  shortUntil: (day: string) => `short until ${day}`,
  untilDay: (day: string) => `Until ${day}`,
  fromHint: (account: string) => `Leave it to use ${account}.`,
  /** v0.7.6: a credit card under "From". */
  cardChip: (card: string) => `${card} (credit card)`,
  cardHint: (statement: string, due: string) =>
    `Charged to the card, not taken from an account. It's on the ${statement} statement, due ${due}.`,
  seeWhatsDue: "See what's due",
  dueBeforePayday: 'Due before payday',
  nothingDue: 'Nothing else is due before payday.',
  seePayments: 'All payments',
  incomeMinusSpent: (income: number, spent: number, due: number) =>
    `${peso(income)} in · ${peso(spent)} spent · ${peso(due)} still due`,
  limitLeft: (left: number, due: number) => `${peso(left)} left in your limit · ${peso(due)} due`,

  // Coming in (v0.5)
  comingIn: 'Coming in',
  comingInNote: "Money you're expecting that isn't income. It never counts in what's left.",
  addExpected: 'Add expected money',
  editExpected: 'Edit expected money',
  expectedName: 'What is it',
  expectedNamePlaceholder: 'Vouchers, leave conversion',
  expectedAmount: 'Amount',
  expectedDate: 'Expected on',
  landsIn: 'Lands in',
  markReceived: 'Received',
  receivedTitleOf: (name: string) => `Received ${name}`,
  receivedOn: 'Day it came',
  receivedExpectedToast: (amount: number, account: string | null) =>
    account ? `Added ${peso(amount)} to ${account}.` : `Marked ${peso(amount)} received.`,
  notYetReceived: 'expected, not yet received',
  tagLate: 'not received yet',
  undoReceived: 'Not received yet',
  deletedExpected: (name: string) => `Deleted ${name}.`,
  nothingComingIn: 'Nothing expected.',
  expectedTotal: (amount: number) => `${peso(amount)} expected`,
  // Repeating Coming in (v0.7.4)
  repeats: 'Repeats',
  repeatNone: 'Once',
  repeatMonthly: 'Every month',
  repeatYearly: 'Every year',
  repeatTag: { none: '', monthly: 'monthly', yearly: 'yearly' } as Record<string, string>,
  firstExpected: 'First one on',
  repeatUntil: 'Last one on or before (optional)',
  repeatUntilHint: 'Leave it empty if it keeps coming.',
  noEnd: 'No end',
  nextOnes: 'Next ones',
  receivedOnes: 'Received',
  skippedOnToast: (day: string) => `Skipped ${day}.`,
  receivedOccurrence: (day: string, amount: number) => `${day} · ${peso(amount)}`,

  // Income sources (v0.5)
  arrivesWith: 'Arrives with a payday',
  arrivesWithNone: 'On its own date',
  arrivesWithHint:
    'It comes on that payday in the months above (moved off weekends the same way), so it lands in the same cutoff.',
  arrivesWithOption: (source: string, slot: string) => `${source} · ${slot} payday`,
  deletedSource: (name: string) => `Deleted ${name}.`,
  deleteSource: (name: string) => `Delete ${name}`,

  // Savings & investments (v0.6)
  savings: 'Savings',
  savingsNote: 'Savings-only accounts. They never count in Safe to spend.',
  interestEstimated: 'Interest (estimated)',
  accountKind: 'Kind',
  accountKinds: {
    spending: 'Spending',
    savings: 'Savings',
    investment: 'Investment',
  } as Record<string, string>,
  useForSpending: 'Use for spending',
  useForSpendingHelp:
    'On: offered in From and counted in Safe to spend. Off: kept under Savings, still open to transfers.',
  annualRate: 'Interest a year (%, optional)',
  annualRateHint: 'Like 3 for 3% a year. Leave it empty for no interest.',
  growth: 'Interest is credited',
  growths: {
    monthly: 'Monthly',
    yearly: 'Yearly',
    maturity: 'At maturity',
  } as Record<string, string>,
  startDate: 'Started on',
  maturityDate: 'Matures on (optional)',
  locked: 'Locked until maturity',
  lockedUntil: (day: string) => `Locked until ${day}`,
  maturesOn: (day: string) => `Matures ${day}`,
  matured: 'Matured',
  ratePerYear: (pct: string) => `${pct}% a year`,
  earnedSoFar: 'Interest so far (estimated)',
  valueIn12: 'In 12 months (estimated)',
  valueAtMaturity: 'At maturity (estimated)',
  interestReceivedTotal: 'Interest received',
  estimateNote:
    'Estimated from the rate on the money in the account. Interest is never income, so it is not in your cash flow.',
  recordInterest: 'Interest received',
  recordInterestHelp:
    'Interest or a dividend that came in. It raises the balance; it is not income.',
  recordedInterest: (amount: number, account: string) =>
    `Added ${peso(amount)} interest to ${account}.`,
  spendingOn: 'Used for spending',
  spendingOff: 'Savings only',
  turnedSpending: (name: string, on: boolean) =>
    on ? `${name} is used for spending now.` : `${name} is savings only now.`,
  toSavings: 'To savings',
  toSavingsNote: 'Goes straight to a savings account, so it is not in what you can spend.',
  goesTo: 'Goes to',
  noSpendingAccounts: 'No accounts used for spending. Turn on Use for spending on one.',

  // Owed to you (v0.6)
  owedToYou: 'Owed to you',
  owedNote: 'Not counted in Safe to spend until you receive it.',
  addReceivable: 'Add',
  addReceivableTitle: 'Add money owed to you',
  editReceivable: 'Edit',
  editReceivableTitle: 'Edit money owed to you',
  whoOwes: 'Who owes you',
  whoOwesPlaceholder: 'Juan, Mom, officemate',
  owedAmount: 'Amount owed',
  expectedOnOptional: 'Expected on (optional)',
  noteOptional: 'Note (optional)',
  nothingOwed: 'No one owes you money.',
  owedTotal: (amount: number) => `${peso(amount)} still to receive`,
  owedLeft: (left: number, of: number) => `${peso(left)} left of ${peso(of)}`,
  paidBack: 'Paid back',
  receive: 'Received',
  receiveTitle: (name: string) => `Received from ${name}`,
  receiveHint: (left: number) => `Up to ${peso(left)}. Part of it is fine.`,
  receivedReceivable: (amount: number, account: string | null) =>
    account ? `Added ${peso(amount)} to ${account}.` : `Marked ${peso(amount)} received.`,
  deletedReceivable: (name: string) => `Deleted ${name}.`,
  undoReceipt: 'Undo',
  receipts: 'Received so far',
  settled: 'Paid back',
  seeOwed: 'See all',

  // Planned spending (Shopping list, from a planned-spending provider)
  plannedLine: (amount: number, label: string) =>
    `${peso(amount)} ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
  plannedNote: 'Planned, not spent yet. It counts once you buy it.',
  /** A period that goes negative is shown as it is, calmly (v0.4). */
  shortNote: (amount: number) =>
    `${peso(amount)} short this period. See which payment can move to the next one, or what can wait.`,
};

/** Goes after the amount: "₱1,750 left until the 9th" or "₱120 over. Starts fresh on the 9th." */
export function leftText(leftMinor: number, nextStart: LocalDate): string {
  return leftMinor < 0
    ? copy.overLine(`on the ${ordinal(Number(nextStart.slice(8, 10)))}`)
    : copy.leftUntil(untilText(nextStart));
}

export function errorText(code: string): string {
  switch (code) {
    case 'name_required':
      return 'Add a name.';
    case 'name_too_long':
      return 'That name is too long (40 characters at most).';
    case 'name_taken':
      return 'You already have one with that name.';
    case 'bad_amount':
      return 'Enter an amount above ₱0.';
    case 'same_account':
      return 'Pick two different accounts.';
    case 'bad_fee':
      return 'Enter a fee above ₱0, or leave it empty.';
    case 'bad_type':
      return 'Pick a type, or none.';
    case 'bad_period':
      return 'Pick a period.';
    case 'bad_schedule':
      return 'Check the paydays: days go from 1 to 31, and the first comes before the second.';
    case 'amounts_mismatch':
      return 'Add an amount for each payday.';
    case 'bad_date':
      return 'Pick a real date.';
    case 'end_before_start':
      return 'The last payday must be on or after the day tracking starts.';
    case 'until_before_start':
      return 'The last time must be on or after the first.';
    case 'has_receipts':
      return 'Some of these were already received or skipped. Undo those first.';
    case 'not_an_occurrence':
      return 'That date is not one of its repeats.';
    case 'bad_repeat':
      return 'Pick how often it repeats.';
    case 'already_received':
      return 'This payday is already confirmed.';
    case 'not_received':
      return "This payday isn't confirmed yet.";
    case 'not_skippable':
      return 'Only bonuses can be skipped.';
    case 'note_too_long':
      return 'That note is too long (200 characters at most).';
    case 'not_found':
      return 'This item is no longer here.';
    case 'bad_rate':
      return 'Enter a rate between 0.01% and 100%, or leave it empty.';
    case 'bad_maturity':
      return 'Pick a maturity date after the start (needed for interest paid at maturity).';
    case 'over_remaining':
      return 'That is more than what is left. Edit the amount owed if it changed.';
    default:
      return "That didn't save. Try again.";
  }
}
